import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { Snackbar, Alert } from '@mui/material';
import { fetchForms, createForm, updateForm, deleteForm } from '../../../Redux/ActionCreators/onboardingAction';
import '../onboarding.scss';
import { Table } from "react-bootstrap";

const CATEGORIES = [
  'Personal information',
  'Compliance & verification',
  'Payroll & benefits',
  'IT & access',
  'Other',
];

const FIELD_TYPES = [
  { value: 'text', label: 'Short Text' },
  { value: 'textarea', label: 'Long Text' },
  { value: 'email', label: 'Email' },
  { value: 'phone', label: 'Phone' },
  { value: 'date', label: 'Date' },
  { value: 'number', label: 'Number' },
  { value: 'select', label: 'Dropdown' },
  { value: 'checkbox', label: 'Checkbox' },
  { value: 'file', label: 'File Upload' },
];

const defaultField = { label: '', type: 'text', required: false, options: '' };
const defaultForm = { name: '', description: '', category: '', fields: [] };

// Convert options arrays from the API back to comma-separated strings for the input field
const normalizeFormForEdit = (form) => {
  if (!form) return defaultForm;
  return {
    ...form,
    category: form.category || '',
    fields: (form.fields || []).map(f => ({
      ...f,
      options: Array.isArray(f.options) ? f.options.join(', ') : (f.options || ''),
    })),
  };
};

// Convert the local state to the shape the API expects
const normalizeFormPayload = (d) => ({
  ...d,
  status: 'active',
  fields: (d.fields || []).map(f => {
    const { id: localId, ...rest } = f;
    const field = {
      ...rest,
      options: typeof f.options === 'string'
        ? f.options.split(',').map(o => o.trim()).filter(Boolean)
        : (Array.isArray(f.options) ? f.options : []),
    };
    // Keep id only if it's a server-generated string (not a local Date.now() number)
    if (localId && typeof localId === 'string') field.id = localId;
    return field;
  }),
});

// ─── Form Builder Modal ───────────────────────────────────────────────────────
const FormBuilderModal = ({ form, onClose, onSaved }) => {
  const dispatch = useDispatch();
  const [data, setData] = useState(() => normalizeFormForEdit(form));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [fieldErrors, setFieldErrors] = useState([]);
  const [categoryError, setCategoryError] = useState(false);
  const errorRef = useRef(null);

  useEffect(() => {
    if (error && errorRef.current) {
      errorRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  }, [error]);

  const setField = (key, val) => setData(d => ({ ...d, [key]: val }));

  const addField = () =>
    setData(d => ({ ...d, fields: [...d.fields, { ...defaultField, id: Date.now() }] }));

  const updateFieldAt = (idx, key, val) => {
    const next = data.fields.map((f, i) => (i === idx ? { ...f, [key]: val } : f));
    setData(d => ({ ...d, fields: next }));
    if (key === 'label' && val.trim()) {
      setFieldErrors(prev => prev.map((err, i) => (i === idx ? { ...err, label: false } : err)));
    }
    if (key === 'options' && val.trim()) {
      setFieldErrors(prev => prev.map((err, i) => (i === idx ? { ...err, options: false } : err)));
    }
  };

  const removeFieldAt = (idx) => {
    setData(d => ({ ...d, fields: d.fields.filter((_, i) => i !== idx) }));
    setFieldErrors(prev => prev.filter((_, i) => i !== idx));
  };

  const moveField = (idx, dir) => {
    const next = [...data.fields];
    const target = idx + dir;
    if (target < 0 || target >= next.length) return;
    [next[idx], next[target]] = [next[target], next[idx]];
    setData(d => ({ ...d, fields: next }));
    // Keep errors in sync with reordered fields
    setFieldErrors(prev => {
      const errs = [...prev];
      [errs[idx], errs[target]] = [errs[target], errs[idx]];
      return errs;
    });
  };

  const handleSave = async () => {
    if (!data.name.trim()) {
      setError('Form name is required.');
      return;
    }

    if (!data.category) {
      setCategoryError(true);
      setError('Category is required.');
      return;
    }

    if (data.fields.length === 0) {
      setError('Please add at least one field before saving.');
      return;
    }

    const newFieldErrors = data.fields.map(f => ({
      label: !f.label.trim(),
      options: f.type === 'select' && !(f.options && String(f.options).trim()),
    }));
    if (newFieldErrors.some(e => e.label || e.options)) {
      setFieldErrors(newFieldErrors);
      if (newFieldErrors.some(e => e.label)) {
        setError('All fields must have a label before saving.');
      } else {
        setError('All dropdown fields must have at least one option.');
      }
      return;
    }

    setCategoryError(false);
    setFieldErrors([]);
    setError('');
    setSaving(true);
    const payload = normalizeFormPayload(data);
    const res = data.id
      ? await dispatch(updateForm(data.id, payload))
      : await dispatch(createForm(payload));
    setSaving(false);
    if (res && !res.__error) {
      onSaved();
    } else {
      const detail =
        Array.isArray(res?.detail) && res.detail.length
          ? res.detail.join(', ')
          : null;
      setError(detail || res?.message || 'Failed to save form.');
    }
  };

  return (
    <div className="upload-modal">
      <div
        className="modal-content"
      >
        <div className="modal-head">
          <div />
          <h3>{data.id ? 'Edit Form' : 'Create Form'}</h3>
          <img
            src="/images/close-icon.svg"
            alt="close"
            onClick={onClose}
            style={{ cursor: 'pointer' }}
            title='Close'
          />
        </div>

        <div style={{ padding: '20px', display: 'flex', flexDirection: 'column', gap: '14px' }}>
          <div style={{ maxHeight: '55vh', overflowY: 'auto', top: '50%', scrollbarWidth: 'none', msOverflowStyle: 'none'}}>
            {/* Form Name */}
            <div className="ob-field">
              <label>
                Form Name <span className="required">*</span>
              </label>
              <input
                type="text"
                placeholder="e.g. Personal Details Form"
                value={data.name}
                onChange={e => setField('name', e.target.value)}
                required
              />
            </div>

            {/* Category */}
            <div className="ob-field">
              <label>Category <span className="required">*</span></label>
              <select
                value={data.category}
                onChange={e => { setField('category', e.target.value); setCategoryError(false); }}
                required
                style={categoryError ? { borderColor: '#ef4444' } : {}}
              >
                <option value="">Select category</option>
                {CATEGORIES.map(c => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
            </div>

            {/* Description */}
            <div className="ob-field">
              <label>
                Description <span className="optional">(optional)</span>
              </label>
              <textarea
                rows={2}
                placeholder="Brief description…"
                value={data.description}
                onChange={e => setField('description', e.target.value)}
              />
            </div>

            {/* Fields header */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom:'12px' }}>
              <span style={{ fontSize: 13, fontWeight: 600, color: '#1a1a2e' }}>Fields</span>
              <button className="ob-btn ob-btn--sm ob-btn--primary" onClick={addField} title='Add field'>
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                  <line x1="12" y1="5" x2="12" y2="19" />
                  <line x1="5" y1="12" x2="19" y2="12" />
                </svg>
                Add field
              </button>
            </div>

            {data.fields.length === 0 && (
              <div
                style={{
                  padding: '20px',
                  textAlign: 'center',
                  color: '#8a9199',
                  fontSize: 13,
                  background: '#f4f5f7',
                  borderRadius: 8,
                }}
              >
                No fields yet. Click "Add Field" to start building your form.
              </div>
            )}

            <div style={{ display: 'flex', flexDirection: 'column', gap: 10, maxHeight: 300, overflowY: 'auto' }}>
              {data.fields.map((field, idx) => (
                <div
                  key={field.id || idx}
                  style={{ padding: '12px 14px', border: '1px solid #e4e9f0', borderRadius: 8, background: '#fff' }}
                >
                  <div style={{ display: 'flex', gap: 10, marginBottom: 8 }}>
                    <input
                      style={{
                        flex: 1,
                        padding: '7px 10px',
                        border: `1px solid ${fieldErrors[idx]?.label ? '#ef4444' : '#e4e9f0'}`,
                        borderRadius: 6,
                        fontSize: 12,
                        outline: 'none',
                        fontFamily: 'inherit',
                      }}
                      placeholder="Field label"
                      value={field.label}
                      onChange={e => updateFieldAt(idx, 'label', e.target.value)}
                    />
                    <select
                      style={{
                        padding: '7px 10px',
                        border: '1px solid #e4e9f0',
                        borderRadius: 6,
                        fontSize: 12,
                        outline: 'none',
                        fontFamily: 'inherit',
                        background: '#fff',
                      }}
                      value={field.type}
                      onChange={e => updateFieldAt(idx, 'type', e.target.value)}
                    >
                      {FIELD_TYPES.map(t => (
                        <option key={t.value} value={t.value}>{t.label}</option>
                      ))}
                    </select>
                  </div>

                  {field.type === 'select' && (
                    <div style={{ marginBottom: 8 }}>
                      <input
                        style={{
                          width: '100%',
                          padding: '7px 10px',
                          border: `1px solid ${fieldErrors[idx]?.options ? '#ef4444' : '#e4e9f0'}`,
                          borderRadius: 6,
                          fontSize: 12,
                          outline: 'none',
                          fontFamily: 'inherit',
                        }}
                        placeholder="Options (comma separated): Option 1, Option 2 *"
                        value={field.options}
                        onChange={e => updateFieldAt(idx, 'options', e.target.value)}
                      />
                      {fieldErrors[idx]?.options && (
                        <span style={{ color: '#ef4444', fontSize: 11, marginTop: 3, display: 'block' }}>
                          At least one option is required for dropdown fields.
                        </span>
                      )}
                    </div>
                  )}

                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <label
                      style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: '#525b66', cursor: 'pointer' }}
                    >
                      <input
                        type="checkbox"
                        checked={field.required}
                        onChange={e => updateFieldAt(idx, 'required', e.target.checked)}
                        style={{ width: 'auto', cursor: 'pointer' }}
                      />
                      Required
                    </label>
                    <div style={{ display: 'flex', gap: 4 }}>
                      <button
                        className="ob-btn ob-btn--icon-only ob-btn--sm"
                        onClick={() => moveField(idx, -1)}
                        title="Move up"
                        disabled={idx === 0}
                      >
                        <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                          <polyline points="18 15 12 9 6 15" />
                        </svg>
                      </button>
                      <button
                        className="ob-btn ob-btn--icon-only ob-btn--sm"
                        onClick={() => moveField(idx, 1)}
                        title="Move down"
                        disabled={idx === data.fields.length - 1}
                      >
                        <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                          <polyline points="6 9 12 15 18 9" />
                        </svg>
                      </button>
                      <button
                        className="ob-btn ob-btn--icon-only ob-btn--sm ob-btn--danger"
                        onClick={() => removeFieldAt(idx)}
                        title="Remove"
                      >
                        <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                          <line x1="18" y1="6" x2="6" y2="18" />
                          <line x1="6" y1="6" x2="18" y2="18" />
                        </svg>
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {error && (
              <div ref={errorRef} className="ob-hint ob-hint--warn" style={{marginTop:'20px'}}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="10" />
                  <line x1="12" y1="8" x2="12" y2="12" />
                  <circle cx="12" cy="16" r="1" fill="currentColor" />
                </svg>
                {error}
              </div>
            )}
          </div>

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, paddingTop: 4 }}>
            <button className="ob-btn" onClick={onClose}>Cancel</button>
            <button className="upload-btn" onClick={handleSave} disabled={saving} style={{ margin: 0 }}>
              {saving ? 'Saving…' : 'Save Form'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const renderCustomPagination = (totalPages, currentPage, setCurrentPage) => {
  const pages = [];
  pages.push(
    <button className="prev-btn" key="prev" onClick={() => setCurrentPage(currentPage - 1)} disabled={currentPage === 1}>
      <img src="/images/leftarrow-icon.svg" alt="" />
    </button>
  );
  pages.push(
    <button key={1} className={currentPage === 1 ? 'active' : ''} onClick={() => setCurrentPage(1)}>{1}</button>
  );
  if (currentPage > 3) pages.push(<button key="left-dots">...</button>);
  const start = Math.max(2, currentPage - 1);
  const end = Math.min(totalPages - 1, currentPage + 1);
  for (let i = start; i <= end; i++) {
    pages.push(<button key={i} className={currentPage === i ? 'active' : ''} onClick={() => setCurrentPage(i)}>{i}</button>);
  }
  if (currentPage < totalPages - 2) pages.push(<button key="right-dots">...</button>);
  if (totalPages > 1) {
    pages.push(
      <button key={totalPages} className={currentPage === totalPages ? 'active' : ''} onClick={() => setCurrentPage(totalPages)}>{totalPages}</button>
    );
  }
  pages.push(
    <button className="next-btn" key="next" onClick={() => setCurrentPage(currentPage + 1)} disabled={currentPage === totalPages || totalPages === 0}>
      <img src="/images/leftarrow-icon.svg" alt="" style={{ rotate: "180deg" }} />
    </button>
  );
  return pages;
};

// ─── Forms Page ───────────────────────────────────────────────────────────────
const FormsPage = () => {
  const dispatch = useDispatch();
  const [forms, setForms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showBuilder, setShowBuilder] = useState(false);
  const [editForm, setEditForm] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [pagination, setPagination] = useState({ total: 0, page: 1, page_size: 10 });
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const debounceRef = useRef(null);

  const loadForms = useCallback(async (page, size, searchTerm = '') => {
    setLoading(true);
    const params = searchTerm ? `search=${encodeURIComponent(searchTerm)}` : '';
    const res = await dispatch(fetchForms(page, size, params));
    if (res) {
      setForms(res.data || []);
      setPagination(res.pagination || { total: (res.data || []).length, page, page_size: size });
    }
    setLoading(false);
  }, [dispatch]);

  useEffect(() => { loadForms(currentPage, pageSize, debouncedSearch); }, [loadForms, currentPage, pageSize, debouncedSearch]);

  const handleSaved = () => {
    setSnackbar({ open: true, message: editForm ? 'Form updated successfully.' : 'Form created successfully.', severity: 'success' });
    setShowBuilder(false);
    setEditForm(null);
    loadForms(currentPage, pageSize, debouncedSearch);
  };

  const handleDelete = async () => {
    setDeleting(true);
    await dispatch(deleteForm(deleteId));
    setDeleteId(null);
    setDeleting(false);
    setSnackbar({ open: true, message: 'Form deleted successfully.', severity: 'success' });
    loadForms(currentPage, pageSize, debouncedSearch);
  };

  const totalPages = Math.ceil((pagination.total || 0) / pageSize);

  return (
    <div className="ob-page">
      <div className="ob-page__header">
        <div className="ob-page__header-left">
          <h2>Forms</h2>
          <p>Manage reusable forms attached to onboarding flows</p>
        </div>
        <button
          className="ob-btn ob-btn--primary"
          onClick={() => { setEditForm(null); setShowBuilder(true); }}
        >
          + Create form
        </button>
      </div>

      <div className="ob-card" style={{ marginBottom: 16 }}>
        <input
          style={{ width: '100%', padding: '9px 12px', border: '1px solid #e4e9f0', borderRadius: 8, fontSize: 13, outline: 'none', fontFamily: 'inherit' }}
          placeholder="Search forms…"
          value={search}
          onChange={e => {
            const val = e.target.value;
            setSearch(val);
            clearTimeout(debounceRef.current);
            debounceRef.current = setTimeout(() => {
              setDebouncedSearch(val);
              setCurrentPage(1);
            }, 400);
          }}
        />
      </div>

      {loading ? (
        <div className="ob-spinner"><div className="spinner-border" role="status" /></div>
      ) : (
        <>
          {forms.length === 0 && (
            <div className="ob-hint ob-hint--warn" style={{ marginBottom: 16 }}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="10" />
                <line x1="12" y1="8" x2="12" y2="12" />
                <circle cx="12" cy="16" r="1" fill="currentColor" />
              </svg>
              <span>
                There are no sample forms. Use <strong>Create form</strong> to build one, save it, then attach it under <strong>Create flow → Forms</strong>.
              </span>
            </div>
          )}

          <div className="ob-table-wrap">
            <Table responsive className="ob-table">
              <thead>
                <tr>
                  <th>Form Name</th>
                  <th>Category</th>
                  <th className='text-center'>Fields</th>
                  <th className='text-center'>Used In</th>
                  <th>Status</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {forms.length === 0 ? (
                  <tr>
                    <td colSpan={6} style={{ textAlign: 'center', color: '#8a9199', padding: '32px 14px' }}>
                      {debouncedSearch ? 'No forms match your search.' : 'No forms yet. Click "+ Create form" to get started.'}
                    </td>
                  </tr>
                ) : (
                  forms.map(form => (
                    <tr key={form.id}>
                      <td className="td-name">{form.name}</td>
                      <td>{form.category || '-'}</td>
                      <td className='text-center'>{form.fields?.length ?? 0}</td>
                      <td className='text-center'>{form.dependent_count ?? 0}</td>
                      <td>
                        <span className="ob-badge ob-badge--green">active</span>
                      </td>
                      <td>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <button
                            className="ob-btn ob-btn--sm"
                            onClick={() => { setEditForm(form); setShowBuilder(true); }}
                          >
                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                              <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                            </svg>
                            Edit
                          </button>
                          <button
                            className="ob-btn ob-btn--icon-only ob-btn--sm ob-btn--danger"
                            onClick={() => setDeleteId(form.id)}
                            title={form.can_delete === false ? (form.can_delete_hint || 'Cannot delete: form is in use') : 'Delete'}
                            disabled={form.can_delete === false}
                            style={{ border: '1px solid #EF44441A', opacity: form.can_delete === false ? 0.45 : 1, cursor: form.can_delete === false ? 'not-allowed' : 'pointer' }}
                          >
                            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <polyline points="3 6 5 6 21 6" />
                              <path d="M19 6l-1 14H6L5 6" />
                              <path d="M10 11v6M14 11v6" />
                              <path d="M9 6V4h6v2" />
                            </svg>
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </Table>
          </div>
          {!loading && pagination.total > 0 && (
            <div className="pagination d-flex flex-wrap align-items-center justify-content-between">
              <div className="d-flex flex-wrap" style={{ gap: '10px' }}>
                <div className="d-flex flex-wrap" style={{ gap: '20px' }}>
                  <div className="pagination-info d-flex align-items-center">
                    <span style={{ color: '#525B66' }}>
                      Showing {(currentPage - 1) * pageSize + 1}–{Math.min(currentPage * pageSize, pagination.total)} of <strong>{pagination.total}</strong> Forms
                    </span>
                  </div>
                  <div className="pagination-buttons">
                    {renderCustomPagination(totalPages, currentPage, setCurrentPage)}
                  </div>
                </div>
                <div className="d-flex align-items-center">
                  <span style={{ color: '#525B66', lineHeight: '100%' }}>Forms per page</span>&nbsp;&nbsp;&nbsp;&nbsp;
                  <select
                    value={pageSize}
                    onChange={e => { setPageSize(parseInt(e.target.value)); setCurrentPage(1); }}
                    style={{ width: '46px', height: '32px', borderRadius: '2px', border: '1px solid #8A9199', color: '#8A9199' }}
                  >
                    <option value={10}>10</option>
                    <option value={25}>25</option>
                    <option value={50}>50</option>
                    <option value={100}>100</option>
                  </select>
                </div>
              </div>
            </div>
          )}
        </>
      )}

      {/* Create / Edit Form Modal */}
      {showBuilder && (
        <FormBuilderModal
          form={editForm}
          onClose={() => { setShowBuilder(false); setEditForm(null); }}
          onSaved={handleSaved}
        />
      )}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar(s => ({ ...s, open: false }))}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar(s => ({ ...s, open: false }))}>
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* Delete Confirmation Modal */}
      {deleteId && (
        <div className="upload-modal">
          <div className="modal-content" style={{ width: '435px' }}>
            <div className="modal-head">
              <div />
              <h3>Delete Form</h3>
              <img
                src="/images/close-icon.svg"
                alt="close"
                onClick={() => setDeleteId(null)}
                style={{ cursor: 'pointer' }}
                title='Close'
              />
            </div>
            <div
              className="modal-input d-flex flex-column align-items-center"
              style={{ gap: '15px', padding: '15px' }}
            >
              <p>Are you sure you want to delete this form? This action cannot be undone.</p>
            </div>
            <div className="d-flex justify-content-center">
              <button className="upload-btn" onClick={handleDelete} disabled={deleting}>
                {deleting ? 'Deleting…' : 'Delete Form'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FormsPage;
